// RegistrationReports.js
// -----------------------------------------------------------------------------
// The Registration "Reports" tab. Two report types — Families and Students —
// each with modular column sections, filters, per-row selection (checkboxes),
// CSV export and print. Kept out of the family detail view so that screen stays
// uncluttered.
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, Printer, Users, GraduationCap } from 'lucide-react';

const val = (v) => (v === null || v === undefined ? '' : String(v));
const joinAr = (a, b) => [a, b].filter(Boolean).join(' ');
const nat = (a, b) => [a, b].filter(Boolean).join(' / ');
const work = (job, sector, company, addr) =>
  [job, sector ? `(${sector})` : '', company, addr].filter(Boolean).join(' · ');
const isAr = (s) => /[؀-ۿ]/.test(val(s));

// --- Families report: modular column sections. Order matters: Registry (القيد)
// comes AFTER Status & Contact (which ends with Address). ---
const FAMILY_MODULES = [
  { key: 'parents', label: 'Parents', cols: [
    { label: 'Father', get: (f) => f.FatherName },
    { label: 'الأب (عربي)', get: (f) => joinAr(f.FatherFirstNameAr, f.FatherLastNameAr) },
    { label: 'Father Nat.', get: (f) => nat(f.FatherNationality1, f.FatherNationality2) },
    { label: 'Father Work', get: (f) => work(f.FatherJob, f.FatherJobSector, f.FatherCompany, f.FatherWorkAddress) },
    { label: 'Mother', get: (f) => f.MotherName },
    { label: 'الأم (عربي)', get: (f) => joinAr(f.MotherFirstNameAr, f.MotherLastNameAr) },
    { label: 'Mother Nat.', get: (f) => nat(f.MotherNationality1, f.MotherNationality2) },
    { label: 'Mother Work', get: (f) => work(f.MotherJob, f.MotherJobSector, f.MotherCompany, f.MotherWorkAddress) },
  ]},
  { key: 'status', label: 'Status & Contact', cols: [
    { label: 'Family Status', get: (f) => f.FamilyStatus },
    { label: 'Religion', get: (f) => f.Religion },
    { label: 'Marital', get: (f) => f.MaritalStatus },
    { label: 'Phone', get: (f) => f.PrimaryPhone || f.FatherPhone || f.MotherPhone },
    { label: 'Email', get: (f) => f.Email },
    { label: 'Address', get: (f) => f.Address },
  ]},
  { key: 'registry', label: 'Registry — القيد', cols: [
    { label: 'المحافظة', get: (f) => f.GovernorateNameAr },
    { label: 'القضاء', get: (f) => f.CazaNameAr },
    { label: 'المحلة', get: (f) => f.LocalityNameAr },
    { label: 'رقم السجل', get: (f) => f.RegisterNumber },
  ]},
  { key: 'children', label: 'Children', cols: [
    { label: '# Children', get: (f) => f.ChildrenCount },
  ]},
];

const STUDENT_MODULES = [
  { key: 'identity', label: 'Student', cols: [
    { label: 'Name', get: (s) => `${val(s.FirstName)} ${val(s.LastName)}`.trim() },
    { label: 'الاسم (عربي)', get: (s) => joinAr(s.FirstNameAr, s.LastNameAr) },
    { label: 'Gender', get: (s) => s.Gender },
    { label: 'DOB', get: (s) => (s.DateOfBirth ? new Date(s.DateOfBirth).toLocaleDateString() : '') },
  ]},
  { key: 'academic', label: 'Academic', cols: [
    { label: 'Year', get: (s) => s.AcademicYear },
    { label: 'Class', get: (s) => s.Class },
    { label: 'Section', get: (s) => s.Section },
    { label: 'Status', get: (s) => s.Status },
  ]},
  { key: 'family', label: 'Family', cols: [
    { label: 'Family ID', get: (s) => s.FamilyID },
    { label: 'Father', get: (s) => s.FatherName },
    { label: 'الأب (عربي)', get: (s) => joinAr(s.FatherFirstNameAr, s.FatherLastNameAr) },
    { label: 'Mother', get: (s) => s.MotherName },
    { label: 'الأم (عربي)', get: (s) => joinAr(s.MotherFirstNameAr, s.MotherLastNameAr) },
  ]},
  { key: 'registry', label: 'Registry — القيد', cols: [
    { label: 'المحافظة', get: (s) => s.GovernorateNameAr },
    { label: 'القضاء', get: (s) => s.CazaNameAr },
    { label: 'المحلة', get: (s) => s.LocalityNameAr },
    { label: 'رقم السجل', get: (s) => s.RegisterNumber },
  ]},
];

const RegistrationReports = ({ apiCall, academicYear, showError }) => {
  const [type, setType] = useState('families'); // 'families' | 'students'
  const [sel, setSel] = useState({ registry: true, parents: true, status: true, children: true, identity: true, academic: true, family: true });
  const [famData, setFamData] = useState(null);
  const [stuData, setStuData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(() => new Set()); // selected row ids

  // filters: global search, a structural academic-year filter, and a modular
  // list of {column, value} filters the user builds themselves.
  const [search, setSearch] = useState('');
  const [year, setYear] = useState(academicYear || ''); // default to current year
  const [filters, setFilters] = useState([]); // [{ label, value }]

  const loadFamilies = useCallback(async () => {
    if (famData) return;
    setLoading(true);
    try { setFamData(await apiCall('/registration/families-report') || []); }
    catch (e) { showError && showError('Load failed: ' + e.message); setFamData([]); }
    finally { setLoading(false); }
  }, [apiCall, famData, showError]);

  const loadStudents = useCallback(async () => {
    if (stuData) return;
    setLoading(true);
    try { setStuData(await apiCall('/registration/students-report?academicYear=') || []); }
    catch (e) { showError && showError('Load failed: ' + e.message); setStuData([]); }
    finally { setLoading(false); }
  }, [apiCall, stuData, showError]);

  useEffect(() => { if (type === 'families') loadFamilies(); }, [type, loadFamilies]);
  useEffect(() => { if (type === 'students') loadStudents(); }, [type, loadStudents]);
  // Switching report type resets the (type-specific) filters + selection.
  useEffect(() => { setFilters([]); setYear(academicYear || ''); setPicked(new Set()); }, [type, academicYear]);

  const modules = type === 'families' ? FAMILY_MODULES : STUDENT_MODULES;
  const chosen = modules.filter((m) => sel[m.key]);
  const idOf = (r) => (type === 'families' ? r.FamilyID : r.StudentID);
  const idLabel = type === 'families' ? 'Family ID' : 'Student ID';

  // Displayed columns (chosen sections) and ALL columns (for the filter picker).
  const cols = useMemo(() => [{ label: idLabel, get: idOf }].concat(chosen.flatMap((m) => m.cols)), [chosen, type]);
  const allCols = useMemo(() => [{ label: idLabel, get: idOf }].concat(modules.flatMap((m) => m.cols)), [modules, type]);

  // A row's academic year(s): students carry one; families carry the set of
  // years their children have records in (AcademicYears = comma list).
  const yearOf = (r) => (type === 'families' ? String(r.AcademicYears || '').split(',').filter(Boolean) : [r.AcademicYear]);

  const years = useMemo(() => {
    const d = type === 'families' ? (famData || []) : (stuData || []);
    const ys = new Set(d.flatMap(yearOf).filter(Boolean));
    if (academicYear) ys.add(academicYear); // keep current year selectable even with no data yet
    return [...ys].sort();
  }, [type, famData, stuData, academicYear]);

  const distinctFor = (label) => {
    const col = allCols.find((c) => c.label === label);
    if (!col) return [];
    const d = type === 'families' ? (famData || []) : (stuData || []);
    return [...new Set(d.map(col.get).map(val).filter(Boolean))].sort().slice(0, 500);
  };

  const rows = useMemo(() => {
    const d = type === 'families' ? (famData || []) : (stuData || []);
    const q = search.trim().toLowerCase();
    const active = filters.filter((f) => f.label && String(f.value).trim());
    const searchFields = type === 'families'
      ? (r) => [r.FamilyID, r.FatherName, r.MotherName, r.FatherFirstNameAr, r.LocalityNameAr]
      : (r) => [r.StudentID, r.FirstName, r.LastName, r.FirstNameAr, r.FamilyID, r.FatherName];
    return d.filter((r) => {
      if (year && !yearOf(r).includes(year)) return false;
      if (q && !searchFields(r).some((x) => val(x).toLowerCase().includes(q))) return false;
      for (const f of active) {
        const col = allCols.find((c) => c.label === f.label);
        if (col && !val(col.get(r)).toLowerCase().includes(String(f.value).toLowerCase())) return false;
      }
      return true;
    });
  }, [type, famData, stuData, search, year, filters, allCols]);

  // A stable, unique key per DISPLAYED row. A student appears once per academic
  // year (StudentID alone is not unique), so students key on ID + year + section.
  const rowKey = (r) => (type === 'families'
    ? val(r.FamilyID)
    : `${val(r.StudentID)}|${val(r.AcademicYear)}|${val(r.Section)}`);

  const allPicked = rows.length > 0 && rows.every((r) => picked.has(rowKey(r)));
  const toggleAll = () => setPicked(allPicked ? new Set() : new Set(rows.map(rowKey)));
  const toggleRow = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // rows to output: selected ones, or all filtered if none selected
  const outRows = picked.size ? rows.filter((r) => picked.has(rowKey(r))) : rows;

  const exportCsv = () => {
    const lines = [cols.map((c) => c.label)].concat(outRows.map((r) => cols.map((c) => val(c.get(r)))));
    const csv = '﻿' + lines.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    a.download = `${type}-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const printTable = () => {
    const esc = (s) => val(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const th = cols.map((c) => `<th>${esc(c.label)}</th>`).join('');
    const tr = outRows.map((r) => `<tr>${cols.map((c) => `<td>${esc(c.get(r))}</td>`).join('')}</tr>`).join('');
    const w = window.open('', '', 'width=1100,height=800');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${type} report</title>
      <style>@page{size:A4 landscape;margin:1cm}body{font-family:Arial,sans-serif;font-size:10px}
      h2{font-size:14px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #999;padding:3px 5px;text-align:left}
      th{background:#eee}</style></head><body>
      <h2>Collège des Apôtres — ${type === 'families' ? 'Families' : 'Students'} Report (${outRows.length})</h2>
      <table><thead><tr>${th}</tr></thead><tbody>${tr}</tbody></table></body></html>`);
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const selBtn = (key, label) => (
    <label key={key} className="flex items-center gap-1.5 text-sm cursor-pointer">
      <input type="checkbox" checked={!!sel[key]} onChange={() => setSel((s) => ({ ...s, [key]: !s[key] }))} className="w-4 h-4" />
      {label}
    </label>
  );

  return (
    <div className="bg-white rounded-lg shadow p-5">
      {/* Report type */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex rounded-lg border p-0.5">
          <button onClick={() => setType('families')} className={`px-4 py-1.5 rounded-md text-sm flex items-center gap-2 ${type === 'families' ? 'bg-slate-800 text-white' : 'text-slate-600'}`}><Users size={15} /> Families</button>
          <button onClick={() => setType('students')} className={`px-4 py-1.5 rounded-md text-sm flex items-center gap-2 ${type === 'students' ? 'bg-slate-800 text-white' : 'text-slate-600'}`}><GraduationCap size={15} /> Students</button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{picked.size ? `${picked.size} selected` : `${rows.length} rows`}</span>
          <button onClick={exportCsv} disabled={!outRows.length} className="px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg flex items-center gap-1 disabled:opacity-50"><Download size={15} /> CSV</button>
          <button onClick={printTable} disabled={!outRows.length} className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg flex items-center gap-1 disabled:opacity-50"><Printer size={15} /> Print</button>
        </div>
      </div>

      {/* Sections + filters */}
      <div className="flex flex-wrap items-center gap-3 mb-2 p-2 bg-gray-50 rounded-lg">
        <span className="text-xs text-gray-500">Columns:</span>
        {modules.map((m) => selBtn(m.key, m.label))}
      </div>
      {/* Filters: search, academic year (both reports), and a modular list of
          column+value filters the user builds. */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="border rounded-lg px-3 py-1.5 text-sm w-52" />
        <select value={year} onChange={(e) => setYear(e.target.value)} className="border rounded-lg px-2 py-1.5 text-sm" title="Academic year">
          <option value="">All years</option>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        {filters.map((f, i) => (
          <div key={i} className="flex items-center gap-1 border rounded-lg pl-2 bg-white">
            <select
              value={f.label}
              onChange={(e) => setFilters((fs) => fs.map((x, idx) => (idx === i ? { label: e.target.value, value: '' } : x)))}
              className="text-sm border-0 focus:ring-0 py-1.5 max-w-[140px]"
            >
              <option value="">Column…</option>
              {allCols.map((c) => <option key={c.label} value={c.label}>{c.label}</option>)}
            </select>
            <input
              list={`dl-${i}`}
              value={f.value}
              onChange={(e) => setFilters((fs) => fs.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)))}
              placeholder="value"
              dir="auto"
              className="text-sm border-0 border-l focus:ring-0 py-1.5 w-32"
            />
            <datalist id={`dl-${i}`}>{distinctFor(f.label).map((v) => <option key={v} value={v} />)}</datalist>
            <button onClick={() => setFilters((fs) => fs.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600 px-1.5" title="Remove filter">×</button>
          </div>
        ))}
        <button onClick={() => setFilters((fs) => [...fs, { label: '', value: '' }])} className="px-2 py-1.5 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50">
          + Filter
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <p className="text-center text-gray-400 py-10">Loading…</p>
      ) : (
        <div className="overflow-auto border rounded-lg" style={{ maxHeight: '60vh' }}>
          <table className="w-full text-xs whitespace-nowrap">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-1.5 border-b w-8"><input type="checkbox" checked={allPicked} onChange={toggleAll} /></th>
                {cols.map((c, i) => <th key={i} className="text-left px-2 py-1.5 font-medium text-gray-600 border-b">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => {
                const k = rowKey(r);
                return (
                  <tr key={`${k}#${ri}`} className={`border-b hover:bg-gray-50 ${picked.has(k) ? 'bg-blue-50' : ''}`}>
                    <td className="px-2 py-1.5"><input type="checkbox" checked={picked.has(k)} onChange={() => toggleRow(k)} /></td>
                    {cols.map((c, i) => { const v = val(c.get(r)); return <td key={i} className="px-2 py-1.5" dir={isAr(v) ? 'rtl' : 'ltr'}>{v || <span className="text-gray-300">—</span>}</td>; })}
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={cols.length + 1} className="text-center text-gray-400 py-8">No rows.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RegistrationReports;
